import { Component } from '@theme/component';

/** @typedef {typeof globalThis} Window */

/**
 * Batch anti-« layout thrashing ».
 *
 * Chaque <product-title> doit LIRE sa hauteur (this.clientHeight + getComputedStyle) puis
 * ÉCRIRE un line-clamp. Fait instance par instance, la lecture de l'une survient juste après
 * l'écriture de la précédente : le navigateur recalcule alors TOUT le layout à chaque fois.
 * Sur la home (48 cartes produit) ça faisait 48 recalculs complets d'un DOM de ~9 800 nœuds
 * -> plusieurs secondes de « Forced reflow » au chargement.
 *
 * On regroupe donc le travail en deux phases dans un seul requestAnimationFrame :
 *   1) toutes les LECTURES (le layout n'est calculé qu'une fois, puis reste propre) ;
 *   2) toutes les ÉCRITURES (aucune lecture entre elles -> aucun recalcul forcé).
 * Résultat : 1 recalcul au lieu de 48.
 */
const pendingTitles = new Set();
let truncationRaf = null;

function flushTruncations() {
  truncationRaf = null;
  const items = [...pendingTitles];
  pendingTitles.clear();

  // Phase 1 — LECTURES uniquement
  const measurements = items.map((el) => el.measureTruncation());
  // Phase 2 — ÉCRITURES uniquement
  items.forEach((el, i) => el.applyTruncation(measurements[i]));
}

function scheduleTruncation(el) {
  pendingTitles.add(el);
  truncationRaf ??= requestAnimationFrame(flushTruncations);
}

/**
 * A component that handles title truncation with responsive behavior
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [text] - The text element to truncate (optional)
 *
 * @extends {Component<Refs>}
 */
class ProductTitle extends Component {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initializeTruncation();
  }

  /**
   * Initialize the title truncation
   */
  #initializeTruncation() {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        scheduleTruncation(this);
      });

      this.resizeObserver.observe(this);
      scheduleTruncation(this);
    } else {
      /** @type {Window} */
      (window).addEventListener('resize', this.#handleResize.bind(this));
      scheduleTruncation(this);
    }
  }

  /**
   * Phase LECTURE : mesure la hauteur disponible et en déduit le nombre de lignes.
   * Ne touche à AUCUN style (aucune écriture) pour ne pas salir le layout.
   * @returns {{ textElement: HTMLElement, maxLines: number } | null}
   */
  measureTruncation() {
    /** @type {HTMLElement} */
    const textElement = this.refs.text || this.querySelector('.title-text') || this;
    if (!textElement.textContent) return null;

    const containerHeight = this.clientHeight;

    const computedStyle = window.getComputedStyle(this);
    const lineHeight = parseFloat(computedStyle.lineHeight);
    const paddingTop = parseFloat(computedStyle.paddingTop);
    const paddingBottom = parseFloat(computedStyle.paddingBottom);

    const availableHeight = containerHeight - paddingTop - paddingBottom;
    const maxLines = Math.max(1, Math.floor(availableHeight / lineHeight));

    return { textElement, maxLines };
  }

  /**
   * Phase ÉCRITURE : applique le line-clamp mesuré. Aucune lecture de géométrie ici.
   * @param {{ textElement: HTMLElement, maxLines: number } | null} measurement
   */
  applyTruncation(measurement) {
    if (!measurement) return;
    const { textElement, maxLines } = measurement;

    textElement.style.display = '-webkit-box';
    textElement.style.webkitBoxOrient = 'vertical';
    textElement.style.overflow = 'hidden';
    textElement.style.textOverflow = 'ellipsis';
    textElement.style.webkitLineClamp = String(maxLines);
  }

  /**
   * Handle window resize events
   */
  #handleResize() {
    scheduleTruncation(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    pendingTitles.delete(this);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    window.removeEventListener('resize', this.#handleResize);
  }
}

if (!customElements.get('product-title')) {
  customElements.define('product-title', ProductTitle);
}
